import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import type { IPageCardsBlockProps, PageCardsBlockItem } from "./PageCardsBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const Link = resolveTootPrimitive("Link");

type PageCardVariant = "featured" | "compact";

const renderMetaTags = (item: PageCardsBlockItem, variant: PageCardVariant) => {
  const tags = item.tags ?? [];

  if (!item.category && tags.length === 0) {
    return null;
  }

  const visibleTags = variant === "compact" ? tags.slice(0, 2) : tags;
  const hiddenTagCount = tags.length - visibleTags.length;

  return (
    <div className="flex flex-wrap gap-[0.45rem]">
      {item.category ? (
        <span className="inline-flex min-h-[1.65rem] items-center rounded-[var(--radius-toot-panel)] border border-toot-orange/45 bg-toot-orange/10 px-[0.62rem] text-[0.64rem] font-[850] uppercase leading-none tracking-[0.16em] text-toot-orange">
          {item.category}
        </span>
      ) : null}
      {visibleTags.map((tag) => (
        <span
          className="inline-flex min-h-[1.65rem] items-center rounded-[var(--radius-toot-panel)] border border-toot-grid-strong/70 bg-toot-surface-raised px-[0.62rem] text-[0.62rem] font-extrabold uppercase leading-none tracking-[0.14em] text-toot-muted"
          key={tag.tag}
        >
          {tag.tag}
        </span>
      ))}
      {hiddenTagCount > 0 ? (
        <span className="inline-flex min-h-[1.65rem] items-center rounded-[var(--radius-toot-panel)] border border-toot-grid-strong/70 bg-toot-surface-raised px-[0.62rem] text-[0.62rem] font-extrabold uppercase leading-none tracking-[0.14em] text-toot-muted">
          +{hiddenTagCount}
        </span>
      ) : null}
    </div>
  );
};

const PageCard = ({
  index,
  item,
  variant,
}: {
  index: number;
  item: PageCardsBlockItem;
  variant: PageCardVariant;
}) => {
  const content = (
    <article
      className={cn(
        "toot-heat-panel group/page-card flex min-h-full flex-col text-[var(--color-foreground)] transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-toot-orange/60 hover:shadow-2xl hover:shadow-black/55 motion-reduce:transition-[border-color,box-shadow] motion-reduce:hover:translate-y-0",
        variant === "compact" &&
          "md:grid md:grid-cols-[minmax(9.5rem,0.42fr)_minmax(0,1fr)] md:grid-rows-[1fr_auto]",
      )}
    >
      {item.image.defaultSrc ? (
        <div
          className={cn(
            "relative overflow-hidden border-b border-toot-grid-strong/60 bg-toot-black",
            variant === "compact" &&
              "md:row-span-2 md:border-b-0 md:border-r md:border-toot-grid-strong/60",
          )}
        >
          <TootMediaFrame
            alt={item.image.alternateText}
            aspectRatio={variant === "featured" ? "16 / 10" : "4 / 3"}
            className={cn(
              "toot-media-heat h-full rounded-none border-0 [&>div]:min-h-full [&>div>div]:w-full [&_img]:transition-transform [&_img]:duration-500 [&_img]:ease-out group-hover/page-card:[&_img]:scale-[1.035] motion-reduce:[&_img]:transition-none motion-reduce:group-hover/page-card:[&_img]:scale-100",
              variant === "compact" && "md:[&>div]:h-full md:[&>div]:min-h-52",
            )}
            focalPoint={item.image.focalPoint ?? undefined}
            height={item.image.height ?? undefined}
            loading={index === 0 ? "eager" : "lazy"}
            sizes={
              item.image.sizes ??
              (variant === "featured"
                ? "(min-width: 1024px) 54vw, 100vw"
                : "(min-width: 1024px) 18vw, 100vw")
            }
            src={item.image.defaultSrc}
            srcSet={item.image.srcSet ?? undefined}
            width={item.image.width ?? undefined}
          />
        </div>
      ) : null}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col",
          variant === "featured" ? "gap-4 p-4" : "gap-[0.7rem] p-[clamp(1rem,1.9vw,1.35rem)]",
        )}
      >
        {renderMetaTags(item, variant)}
        {item.heading ? (
          <h3
            className={cn(
              "m-0 text-balance font-black text-[var(--color-foreground)]",
              variant === "featured"
                ? "text-[1.25rem] md:text-[2.1rem] leading-[1.02] tracking-[-0.04em]"
                : "text-[1.2rem] md:text-[1.55rem] leading-[1.05] tracking-[-0.035em]",
            )}
          >
            {item.heading}
          </h3>
        ) : null}
        <RichTextHtml
          className={cn(
            "m-0 max-w-[40rem] text-pretty text-base leading-[1.75] text-toot-muted [&>*]:m-0 [&>*+*]:mt-3",
            variant === "compact" && "text-sm leading-[1.65]",
          )}
          html={item.subheading}
        />
      </div>
      {item.link.text ? (
        <div
          className={cn(
            "flex min-h-[3.2rem] items-center justify-between gap-4 border-t border-toot-grid-strong/60 px-[clamp(1rem,1.9vw,1.35rem)] py-[0.85rem] text-[0.86rem] font-[850] leading-[1.2] tracking-[0.02em] text-toot-orange",
            variant === "compact" && "md:col-start-2",
          )}
        >
          <span>{item.link.text}</span>
          <span aria-hidden="true">→</span>
        </div>
      ) : null}
    </article>
  );

  if (!item.link.href) {
    return content;
  }

  return (
    <Link
      aria-label={item.link.ariaLabel}
      className="block h-full rounded-[var(--radius-toot-frame)] text-inherit no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-ring)] focus-visible:[&>article]:border-toot-orange"
      href={item.link.href}
      rel={item.link.target === "_blank" ? "noopener noreferrer" : undefined}
      target={item.link.target}
    >
      {content}
    </Link>
  );
};

const getPageCardsGridClassName = (count: number) => {
  if (count === 2) return "md:grid-cols-2";
  if (count % 4 === 0) return "md:grid-cols-2 xl:grid-cols-4";
  return "md:grid-cols-2 xl:grid-cols-3";
};

export const PageCardsBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
  subheading,
}: IPageCardsBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const hasHeader = Boolean(eyebrow || heading || subheading);
  const header = hasHeader ? (
    heading ? (
      <TootHeading
        alignment="center"
        className="max-w-[min(100%,61rem)]"
        eyebrow={eyebrow}
        heading={heading}
        subheading={subheading}
      />
    ) : (
      <div className="mx-auto flex max-w-[min(100%,61rem)] flex-col items-center gap-4 text-center">
        {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
        {subheading ? <RichTextHtml className="toot-subheading" html={subheading} /> : null}
      </div>
    )
  ) : null;

  return (
    <TootSectionShell
      actions={
        buttons?.length ? (
          <TootActions
            actions={buttons}
            alignment="center"
            className="[&_.toot-button]:min-h-12 [&_.toot-button]:rounded-[var(--radius-toot-panel)]"
          />
        ) : null
      }
      className="space-y-8"
      header={header}
      id={anchorId ?? undefined}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      tone="dark"
      width="wide"
    >
      <div
        className={cn(
          "grid auto-rows-fr items-stretch gap-[clamp(1rem,2vw,1.5rem)]",
          getPageCardsGridClassName(items.length),
        )}
      >
        {items.map((item, index) => (
          <PageCard index={index} item={item} key={`${item.heading}-${index}`} variant="featured" />
        ))}
      </div>
    </TootSectionShell>
  );
};

PageCardsBlock.displayName = "Block::PageCardsBlock";
